import React, {useState, useEffect} from 'react'
import {OrgService, Organization} from 'app/services/OrgService'
import {SponsorService} from 'app/services/SponsorService'
import {Badge, Modal, ProgressBar, Button, Form} from 'react-bootstrap-v5'
import {VJFPUserService} from 'app/services/VJFPUserService'
import {VJFPUser} from 'app/API'
import {AdminRoutes} from './AdminPage'
import {useHistory} from 'react-router-dom'
import {AppTopNav} from 'app/modules/shared/AppTopNav'
import {BasePageProps, Sponsor} from 'app/types'
import moment from 'moment'
import {KTSVG} from '_metronic/helpers'
import {Storage} from 'aws-amplify'

export const AdminOrganizations: React.FC<BasePageProps> = (props: BasePageProps) => {
  const [allOrgs, setAllOrgs] = useState<Organization[]>([])
  const [sponsors, setSponsors] = useState<{id: string; name: string}[]>([])
  const [showModal, setShowModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined)
  const [hasMore, setHasMore] = useState(true)
  const [userDetails, setUserDetails] = useState<Record<string, VJFPUser>>({})
  const [sortBy, setSortBy] = useState<'date' | 'companyName'>('companyName')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [loadingError, setLoadingError] = useState<string | null>(null)
  const [loadingRows, setLoadingRows] = useState<Set<string>>(new Set())
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('grid')
  const [logoUrls, setLogoUrls] = useState<Record<string, string>>({})
  const [avatarUrls, setAvatarUrls] = useState<Record<string, string>>({})
  const history = useHistory()

  const orgService = new OrgService()
  const sponsorService = new SponsorService()
  const vjfpUserService = new VJFPUserService()

  const loadOrganizations = async (
    loadMore = false,
    sortBy: 'date' | 'companyName',
    sortDirection: 'asc' | 'desc'
  ) => {
    try {
      setIsLoading(true)
      setLoadingError(null)

      const {organizations, cursor} = await orgService.getAllOrganizations(
        5,
        sortBy,
        sortDirection,
        loadMore ? nextCursor : undefined
      )

      if (loadMore && organizations.length === 0) {
        setHasMore(false)
        return
      }

      if (loadMore) {
        setAllOrgs((prev) => [...prev, ...organizations])
      } else {
        setAllOrgs(organizations)
      }

      setNextCursor(cursor)
      setHasMore(!!cursor)

      const userPromises = organizations.map(async (org) => {
        setLoadingRows((prev) => new Set([...prev, org.id]))
        try {
          const userDetails = await vjfpUserService.getById(org.id, org.createdBy)
          setUserDetails((prev) => ({...prev, [org.createdBy]: userDetails}))
        } finally {
          setLoadingRows((prev) => {
            const next = new Set([...prev])
            next.delete(org.id)
            return next
          })
        }
      })
      await Promise.all(userPromises)

      // Fetch logo and avatar URLs
      const logoAndAvatarPromises = organizations.map(async (org) => {
        const promises = []

        // Logo
        if (org.companyLogo) {
          promises.push(
            (async () => {
              try {
                const url = await Storage.get(org.companyLogo, {expires: 3600})
                setLogoUrls((prev) => ({...prev, [org.id]: url}))
              } catch (err) {
                console.error(`Error fetching logo for ${org.id}:`, err)
              }
            })()
          )
        }

        // Avatar for the creator
        if (userDetails[org.createdBy]?.avatar) {
          promises.push(
            (async () => {
              try {
                const url = await Storage.get(userDetails[org.createdBy].avatar!, {expires: 3600})
                setAvatarUrls((prev) => ({...prev, [org.createdBy]: url}))
              } catch (err) {
                console.error(`Error fetching avatar for ${org.createdBy}:`, err)
              }
            })()
          )
        } else if (org.createdBy) {
          // Fallback to fetch user if not already in state to get avatar key
          const user = await vjfpUserService.getById(org.id, org.createdBy)
          if (user?.avatar) {
            promises.push(
              (async () => {
                try {
                  const url = await Storage.get(user.avatar!, {expires: 3600})
                  setAvatarUrls((prev) => ({...prev, [org.createdBy]: url}))
                } catch (err) {
                  console.error(`Error fetching avatar for ${org.createdBy}:`, err)
                }
              })()
            )
          }
        }

        return Promise.all(promises)
      })
      await Promise.all(logoAndAvatarPromises)
    } catch (error) {
      console.error('Error loading companies:', error)
      setLoadingError(error instanceof Error ? error.message : 'Failed to load organizations')
      setHasMore(false)
    } finally {
      setIsLoading(false)
    }
  }

  const loadSponsors = async () => {
    try {
      let _sponsors: Sponsor[] = [],
        limit: number = 10,
        cursor: string | any = undefined
      do {
        const result = await sponsorService.getAllSponsors(limit, cursor)
        cursor = result.cursor
        _sponsors = [..._sponsors, ...result.sponsors]
      } while (cursor != null)

      setSponsors(_sponsors.map((sponsor) => ({id: sponsor.id, name: sponsor.name})))
    } catch (error) {
      console.error('Error loading sponsors:', error)
    }
  }

  const handleSponsorChange = async (orgId: string, sponsorId: string) => {
    try {
      const selectedSponsor = sponsors.find((sponsor) => sponsor.id === sponsorId)
      if (selectedSponsor) {
        await orgService.updateOrg(orgId, {
          sponsorId: selectedSponsor.id,
          sponsorName: selectedSponsor.name,
        })
        setAllOrgs((prevOrgs) =>
          prevOrgs.map((org) =>
            org.id === orgId
              ? {...org, sponsorId: selectedSponsor.id, sponsorName: selectedSponsor.name}
              : org
          )
        )
      }
    } catch (error) {
      console.error('Error updating company with sponsor:', error)
    }
  }

  const getOnboardingBadge = (step: string) => {
    const badgeConfig: Record<string, {bg: string; text: any; label: string}> = {
      FilledOrganizationDetails: {bg: 'info', text: 'white', label: 'Company Details Filled'},
      CreatedJobFair: {bg: 'primary', text: 'white', label: 'Job Fair Created'},
      JobFairPaymentCompleted: {bg: 'success', text: 'white', label: 'Payment Completed'},
      BoothConfigured: {bg: 'warning', text: 'dark', label: 'Booth Configured'},
      AssignedARecruiter: {bg: 'success', text: 'white', label: 'Recruiters Assigned'},
    }
    return badgeConfig[step] || {bg: 'secondary', text: 'white', label: step}
  }

  const getProgressColor = (progress: number) => {
    if (progress >= 80) return 'success'
    if (progress >= 50) return 'primary'
    if (progress >= 25) return 'warning'
    return 'danger'
  }

  useEffect(() => {
    loadOrganizations(false, sortBy, sortDirection)
    loadSponsors()
  }, [])

  return (
    <div className='chatPage'>
      <AppTopNav title={props.title} breadcrumbs={props.breadcrumbs} />

      <div className='container-fluid mt-10'>
        {/* Header Card with Statistics */}
        <div className='row g-5 g-xl-8 mb-6'>
          <div className='col-md-6 col-xl-3'>
            <div className='card card-flush shadow-sm h-lg-100 bg-white border-top border-primary border-4'>
              <div className='card-body d-flex flex-column justify-content-center p-5'>
                <div className='d-flex align-items-center'>
                  <div className='symbol symbol-40px me-4'>
                    <div className='symbol-label bg-light-primary'>
                      <KTSVG
                        path='/media/icons/duotone/Communication/Group.svg'
                        className='svg-icon-1 svg-icon-primary'
                      />
                    </div>
                  </div>
                  <div className='d-flex flex-column'>
                    <span className='text-gray-900 fw-bolder fs-3 lh-1'>{allOrgs.length}</span>
                    <span className='text-gray-500 fw-bold fs-7 text-uppercase ls-1 mt-2'>
                      Total Organizations
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='col-md-6 col-xl-3'>
            <div className='card card-flush shadow-sm h-lg-100 bg-white border-top border-success border-4'>
              <div className='card-body d-flex flex-column justify-content-center p-5'>
                <div className='d-flex align-items-center'>
                  <div className='symbol symbol-40px me-4'>
                    <div className='symbol-label bg-light-success'>
                      <KTSVG
                        path='/media/icons/duotone/Communication/Clipboard-check.svg'
                        className='svg-icon-1 svg-icon-success'
                      />
                    </div>
                  </div>
                  <div className='d-flex flex-column'>
                    <span className='text-gray-900 fw-bolder fs-3 lh-1'>
                      {allOrgs.filter((org) => org.onBoardingProgress.progress === 100).length}
                    </span>
                    <span className='text-gray-500 fw-bold fs-7 text-uppercase ls-1 mt-2'>
                      Fully Onboarded
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='col-md-6 col-xl-3'>
            <div className='card card-flush shadow-sm h-lg-100 bg-white border-top border-warning border-4'>
              <div className='card-body d-flex flex-column justify-content-center p-5'>
                <div className='d-flex align-items-center'>
                  <div className='symbol symbol-40px me-4'>
                    <div className='symbol-label bg-light-warning'>
                      <KTSVG
                        path='/media/icons/duotone/General/Half-star.svg'
                        className='svg-icon-1 svg-icon-warning'
                      />
                    </div>
                  </div>
                  <div className='d-flex flex-column'>
                    <span className='text-gray-900 fw-bolder fs-3 lh-1'>
                      {allOrgs.filter((org) => org.sponsorId).length}
                    </span>
                    <span className='text-gray-500 fw-bold fs-7 text-uppercase ls-1 mt-2'>
                      With Sponsors
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='col-md-6 col-xl-3'>
            <div className='card card-flush shadow-sm h-lg-100 bg-white border-top border-info border-4'>
              <div className='card-body d-flex flex-column justify-content-center p-5'>
                <div className='d-flex align-items-center'>
                  <div className='symbol symbol-40px me-4'>
                    <div className='symbol-label bg-light-info'>
                      <KTSVG
                        path='/media/icons/duotone/Code/Time-schedule.svg'
                        className='svg-icon-1 svg-icon-info'
                      />
                    </div>
                  </div>
                  <div className='d-flex flex-column'>
                    <span className='text-gray-900 fw-bolder fs-3 lh-1'>
                      {allOrgs.filter((org) => org.onBoardingProgress.progress < 100).length}
                    </span>
                    <span className='text-gray-500 fw-bold fs-7 text-uppercase ls-1 mt-2'>
                      In Progress
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Card */}
        <div className='row mb-5'>
          <div className='col'>
            <div className='card card-custom shadow-sm'>
              {/* Card Header */}
              <div className='card-header border-0 pt-6'>
                {/* Removed Title for Compactness */}
                <div className='card-toolbar'>
                  <div className='d-flex justify-content-end align-items-center gap-3'>
                    {/* View Mode Toggle */}
                    <div className='btn-group btn-group-sm'>
                      <button
                        type='button'
                        className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-light'}`}
                        onClick={() => setViewMode('table')}
                        title='Table View'
                      >
                        <KTSVG
                          path='/media/icons/duotone/Layout/Layout-4-blocks.svg'
                          className='svg-icon-3'
                        />
                      </button>
                      <button
                        type='button'
                        className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-light'}`}
                        onClick={() => setViewMode('grid')}
                        title='Grid View'
                      >
                        <KTSVG
                          path='/media/icons/duotone/Layout/Layout-grid.svg'
                          className='svg-icon-3'
                        />
                      </button>
                    </div>

                    <div className='d-flex align-items-center gap-2'>
                      <label className='form-label fw-semibold mb-0 text-nowrap'>Sort By:</label>
                      <select
                        className='form-select form-select-solid form-select-sm w-150px'
                        value={sortBy}
                        onChange={(e) => {
                          setSortBy((e.target as HTMLSelectElement).value as 'date' | 'companyName')
                          loadOrganizations(
                            false,
                            (e.target as HTMLSelectElement).value as 'date' | 'companyName',
                            sortDirection
                          )
                        }}
                      >
                        <option value='date'>Date</option>
                        <option value='companyName'>Company Name</option>
                      </select>
                      <button
                        type='button'
                        className='btn btn-icon btn-active-light-primary btn-sm'
                        onClick={() => {
                          const newDirection = sortDirection === 'asc' ? 'desc' : 'asc'
                          setSortDirection(newDirection)
                          loadOrganizations(false, sortBy, newDirection)
                        }}
                        title={`Sort ${sortDirection === 'asc' ? 'Descending' : 'Ascending'}`}
                      >
                        <KTSVG
                          path={
                            sortDirection === 'asc'
                              ? '/media/icons/duotone/Navigation/Up-2.svg'
                              : '/media/icons/duotone/Navigation/Down-2.svg'
                          }
                          className='svg-icon-2'
                        />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card Body */}
              <div className='card-body pt-0'>
                {loadingError && (
                  <div className='alert alert-danger d-flex align-items-center justify-content-between mb-5'>
                    <div className='d-flex align-items-center'>
                      <KTSVG
                        path='/media/icons/duotone/Code/Error-circle.svg'
                        className='svg-icon-2x svg-icon-danger me-4'
                      />
                      <div>
                        <h5 className='mb-1'>Error Loading Organizations</h5>
                        <div className='text-gray-700'>{loadingError}</div>
                      </div>
                    </div>
                    <button
                      className='btn btn-danger btn-sm'
                      onClick={() => loadOrganizations(false, sortBy, sortDirection)}
                    >
                      <KTSVG
                        path='/media/icons/duotone/General/Update.svg'
                        className='svg-icon-3 me-1'
                      />
                      Retry
                    </button>
                  </div>
                )}

                {/* Table View */}
                {viewMode === 'table' && (
                  <div className='table-responsive'>
                    <table className='table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3'>
                      <thead>
                        <tr className='fw-bold text-muted bg-light'>
                          <th className='min-w-200px ps-4 rounded-start'>Organization</th>
                          <th className='min-w-200px'>Employer Admin & Contact</th>
                          <th className='min-w-120px text-center'>Created Date</th>
                          <th className='min-w-180px text-center'>Assigned Sponsor</th>
                          <th className='min-w-200px text-center'>Onboarding Status</th>
                          <th className='min-w-80px text-center rounded-end'>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allOrgs.map((org) => {
                          const badge = getOnboardingBadge(org.onBoardingProgress.lastCompletedStep)
                          const progressVariant = getProgressColor(org.onBoardingProgress.progress)
                          const logoUrl = logoUrls[org.id]

                          return (
                            <tr
                              key={org.id}
                              className={loadingRows.has(org.id) ? 'opacity-50' : ''}
                            >
                              <td className='ps-4'>
                                <div className='d-flex align-items-center'>
                                  <div className='w-80px h-50px bg-light rounded border border-gray-200 d-flex align-items-center justify-content-center overflow-hidden me-4 flex-shrink-0'>
                                    {logoUrl ? (
                                      <img
                                        src={logoUrl}
                                        alt={org.companyName}
                                        className='mw-100 mh-100 p-1 object-fit-contain'
                                      />
                                    ) : (
                                      <KTSVG
                                        path='/media/icons/duotone/Home/Building.svg'
                                        className='svg-icon-1 svg-icon-primary'
                                      />
                                    )}
                                  </div>
                                  <div className='d-flex flex-column'>
                                    <span className='text-dark fw-bold fs-5 text-hover-primary cursor-pointer'>
                                      {org.companyName}
                                    </span>
                                    <div className='d-flex align-items-center mt-1'>
                                      <KTSVG
                                        path='/media/icons/duotone/Map/Marker1.svg'
                                        className='svg-icon-6 me-1 text-muted'
                                      />
                                      <span className='text-muted fw-semibold fs-7'>
                                        {org.city}, {org.state}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td>
                                {loadingRows.has(org.id) ? (
                                  <div className='d-flex align-items-center text-muted fs-7'>
                                    <span className='spinner-border spinner-border-sm me-2'></span>
                                    Loading...
                                  </div>
                                ) : userDetails[org.createdBy] ? (
                                  <div className='d-flex flex-column'>
                                    <div className='d-flex align-items-center mb-1'>
                                      <div className='symbol symbol-35px me-3'>
                                        {avatarUrls[org.createdBy] ? (
                                          <img
                                            src={avatarUrls[org.createdBy]}
                                            alt='Avatar'
                                            className='rounded-circle'
                                          />
                                        ) : (
                                          <div className='symbol-label bg-light-primary fs-7 fw-bold text-primary'>
                                            {userDetails[org.createdBy]?.firstName?.charAt(0)}
                                            {userDetails[org.createdBy]?.lastName?.charAt(0)}
                                          </div>
                                        )}
                                      </div>
                                      <span className='text-gray-800 fw-bold fs-6'>
                                        {userDetails[org.createdBy].firstName}{' '}
                                        {userDetails[org.createdBy].lastName}
                                      </span>
                                    </div>
                                    <div className='d-flex flex-column ms-1 mt-1'>
                                      <a
                                        href={'mailto:' + userDetails[org.createdBy].emailId}
                                        className='text-muted text-hover-primary fs-7 mb-1 d-flex align-items-center'
                                      >
                                        <KTSVG
                                          path='/media/icons/duotone/Communication/Mail.svg'
                                          className='svg-icon-7 me-2'
                                        />
                                        {userDetails[org.createdBy].emailId}
                                      </a>
                                      <span className='text-muted fs-7 d-flex align-items-center'>
                                        <KTSVG
                                          path='/media/icons/duotone/Communication/Call.svg'
                                          className='svg-icon-7 me-2'
                                        />
                                        {userDetails[org.createdBy].phone_number}
                                      </span>
                                    </div>
                                  </div>
                                ) : (
                                  <span className='text-muted fs-7'>Unknown</span>
                                )}
                              </td>
                              <td className='text-center'>
                                <div className='d-flex flex-column align-items-center justify-content-center'>
                                  <span className='text-gray-800 fw-semibold fs-6'>
                                    {moment(org.createdAt).format('MMM DD, YYYY')}
                                  </span>
                                  <span className='text-muted fs-7'>
                                    {moment(org.createdAt).format('hh:mm A')}
                                  </span>
                                </div>
                              </td>
                              <td className='text-center'>
                                <div className='d-flex align-items-center justify-content-center'>
                                  <Form.Select
                                    value={org.sponsorId || ''}
                                    onChange={(e) =>
                                      handleSponsorChange(
                                        org.id,
                                        (e.target as HTMLSelectElement).value
                                      )
                                    }
                                    className='form-select form-select-transparent border-0 fw-bold fs-6 text-gray-800 text-center w-auto'
                                  >
                                    <option value=''>No Sponsor</option>
                                    {sponsors.map((sponsor) => (
                                      <option key={sponsor.id} value={sponsor.id}>
                                        {sponsor.name}
                                      </option>
                                    ))}
                                  </Form.Select>
                                </div>
                              </td>
                              <td>
                                <div className='d-flex flex-column gap-2 px-3 justify-content-center'>
                                  <div className='d-flex justify-content-between align-items-center'>
                                    <span
                                      className={`badge badge-light-${progressVariant} fs-7 fw-bold px-3 py-1`}
                                    >
                                      {org.onBoardingProgress.progress}%
                                    </span>
                                    <Badge
                                      bg={badge.bg}
                                      text={badge.text}
                                      className='px-3 py-1 fs-8 fw-bold rounded-pill'
                                    >
                                      {badge.label}
                                    </Badge>
                                  </div>
                                  <div className='h-6px w-100 bg-light-success rounded overflow-hidden mt-1'>
                                    <div
                                      className={`bg-${progressVariant} rounded h-6px`}
                                      role='progressbar'
                                      style={{width: `${org.onBoardingProgress.progress}%`}}
                                    ></div>
                                  </div>
                                </div>
                              </td>
                              <td className='text-center'>
                                <button
                                  className='btn btn-light-primary btn-sm fw-bold'
                                  onClick={() =>
                                    history.push(AdminRoutes.Jobfairs + '?orgId=' + org.id)
                                  }
                                >
                                  Manage
                                </button>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Grid View */}
                {viewMode === 'grid' && (
                  <div className='row g-5 g-xl-6'>
                    {allOrgs.map((org) => {
                      const badge = getOnboardingBadge(org.onBoardingProgress.lastCompletedStep)
                      const progressVariant = getProgressColor(org.onBoardingProgress.progress)
                      const logoUrl = logoUrls[org.id]

                      return (
                        <div key={org.id} className='col-md-6 col-xl-4'>
                          <div
                            className={`card card-flush shadow-sm h-100 position-relative border-0 hover-elevate-up transition-all ${
                              loadingRows.has(org.id) ? 'opacity-50' : ''
                            }`}
                          >
                            {/* Organization Header */}
                            <div className='card-header pt-6 min-h-auto'>
                              <div className='d-flex align-items-center w-100'>
                                <div className='w-80px h-50px bg-light rounded border border-gray-200 d-flex align-items-center justify-content-center overflow-hidden me-4 flex-shrink-0'>
                                  {logoUrl ? (
                                    <img
                                      src={logoUrl}
                                      alt={org.companyName}
                                      className='mw-100 mh-100 p-1 object-fit-contain'
                                    />
                                  ) : (
                                    <KTSVG
                                      path='/media/icons/duotone/Home/Building.svg'
                                      className='svg-icon-2 svg-icon-primary'
                                    />
                                  )}
                                </div>
                                <div className='flex-grow-1 overflow-hidden'>
                                  <div className='d-flex flex-column'>
                                    <span
                                      className='text-gray-900 fw-bolder fs-5 text-truncate d-inline-block w-100'
                                      title={org.companyName}
                                    >
                                      {org.companyName}
                                    </span>
                                    <div className='d-flex align-items-center mt-2'>
                                      <KTSVG
                                        path='/media/icons/duotone/Map/Marker1.svg'
                                        className='svg-icon-7 me-1 text-muted'
                                      />
                                      <span className='text-muted fw-bold fs-7 text-truncate'>
                                        {org.city}, {org.state}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Card Body */}
                            <div className='card-body py-4'>
                              <div className='separator separator-dashed border-gray-300 mb-5'></div>

                              <div className='d-flex flex-stack mb-4'>
                                <div className='d-flex align-items-center' title='Created Date'>
                                  <KTSVG
                                    path='/media/icons/duotone/Code/Time-schedule.svg'
                                    className='svg-icon-3 me-2 text-gray-500'
                                  />
                                  <span className='text-gray-800 fw-bold fs-7'>
                                    {moment(org.createdAt).format('MMM DD, YYYY')}
                                  </span>
                                </div>
                                <div className='d-flex align-items-center text-end'>
                                  {avatarUrls[org.createdBy] ? (
                                    <div className='symbol symbol-25px me-2'>
                                      <img
                                        src={avatarUrls[org.createdBy]}
                                        alt='Avatar'
                                        className='rounded-circle'
                                      />
                                    </div>
                                  ) : (
                                    <div className='symbol symbol-25px me-2'>
                                      <div className='symbol-label bg-light-primary fs-9 fw-bold text-primary'>
                                        {userDetails[org.createdBy]?.firstName?.charAt(0)}
                                        {userDetails[org.createdBy]?.lastName?.charAt(0)}
                                      </div>
                                    </div>
                                  )}
                                  <span
                                    className='text-gray-800 fw-bold fs-7 text-truncate max-w-120px'
                                    title={
                                      userDetails[org.createdBy]
                                        ? `${userDetails[org.createdBy].firstName} ${
                                            userDetails[org.createdBy].lastName
                                          }`
                                        : ''
                                    }
                                  >
                                    {userDetails[org.createdBy]
                                      ? `${userDetails[org.createdBy].firstName} ${
                                          userDetails[org.createdBy].lastName
                                        }.`
                                      : ''}
                                  </span>
                                </div>
                              </div>

                              <div className='d-flex align-items-center bg-gray-100 rounded p-3 mb-5'>
                                <KTSVG
                                  path='/media/icons/duotone/General/Half-star.svg'
                                  className='svg-icon-3 svg-icon-primary me-3'
                                />
                                <Form.Select
                                  value={org.sponsorId || ''}
                                  onChange={(e) =>
                                    handleSponsorChange(
                                      org.id,
                                      (e.target as HTMLSelectElement).value
                                    )
                                  }
                                  className='form-select form-select-transparent border-0 fw-bold p-0 fs-7 text-gray-800'
                                >
                                  <option value=''>No Sponsor</option>
                                  {sponsors.map((sponsor) => (
                                    <option key={sponsor.id} value={sponsor.id}>
                                      {sponsor.name}
                                    </option>
                                  ))}
                                </Form.Select>
                              </div>

                              <div className='d-flex flex-column mt-auto'>
                                <div className='d-flex flex-stack mb-2'>
                                  <span className='text-gray-400 fw-bold fs-8 text-uppercase ls-1'>
                                    Onboarding
                                  </span>
                                  <span
                                    className={`badge badge-light-${progressVariant} fs-8 fw-bolder px-3 py-1`}
                                  >
                                    {org.onBoardingProgress.progress}%
                                  </span>
                                </div>
                                <div className='h-6px w-100 bg-light-success rounded overflow-hidden'>
                                  <div
                                    className={`bg-${progressVariant} rounded h-6px`}
                                    role='progressbar'
                                    style={{width: `${org.onBoardingProgress.progress}%`}}
                                  ></div>
                                </div>
                                <div className='mt-3 d-flex flex-stack'>
                                  <Badge
                                    bg={badge.bg}
                                    text={badge.text}
                                    className='px-3 py-1 fs-8 fw-bold rounded-pill text-uppercase'
                                  >
                                    {badge.label}
                                  </Badge>
                                  <button
                                    className='btn btn-light-primary btn-sm fw-bold px-4 py-2'
                                    onClick={() =>
                                      history.push(AdminRoutes.Jobfairs + '?orgId=' + org.id)
                                    }
                                  >
                                    Manage{' '}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}

                {/* Load More Section */}
                {hasMore && (
                  <div className='text-center mt-8'>
                    <Button
                      onClick={() => loadOrganizations(true, sortBy, sortDirection)}
                      disabled={isLoading}
                      className='btn btn-primary'
                    >
                      {isLoading ? (
                        <>
                          <span
                            className='spinner-border spinner-border-sm me-2'
                            role='status'
                            aria-hidden='true'
                          ></span>
                          Loading More Organizations...
                        </>
                      ) : (
                        <>
                          <KTSVG
                            path='/media/icons/duotone/Navigation/Down-2.svg'
                            className='svg-icon-3 me-2'
                          />
                          Load More Organizations
                        </>
                      )}
                    </Button>
                  </div>
                )}

                {!hasMore && allOrgs.length > 0 && (
                  <div className='separator separator-dashed my-6'></div>
                )}

                {!hasMore && allOrgs.length > 0 && (
                  <div className='text-center'>
                    <div className='text-muted fw-semibold fs-6'>
                      <KTSVG
                        path='/media/icons/duotone/Navigation/Check.svg'
                        className='svg-icon-3 me-2'
                      />
                      All {allOrgs.length} organizations loaded
                    </div>
                  </div>
                )}

                {allOrgs.length === 0 && !isLoading && !loadingError && (
                  <div className='text-center py-10'>
                    <KTSVG
                      path='/media/icons/duotone/Files/Empty-file.svg'
                      className='svg-icon-5x svg-icon-muted mb-5'
                    />
                    <div className='text-muted fw-bold fs-4'>No organizations found</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <Modal show={showModal} onHide={() => setShowModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Edit Company</Modal.Title>
          </Modal.Header>
          <Modal.Body></Modal.Body>
        </Modal>
      </div>
    </div>
  )
}
